import React, { useState } from "react";
import { Search, LocateFixed, ChevronDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { GENRES } from "@/lib/constants";

export default function SearchBar({ value, onChange, onSubmit, onLocate, locating }) {
  const { t } = useApp();
  const set = (k, v) => onChange({ ...value, [k]: v });
  const [gOpen, setGOpen] = useState(false);
  const gl = (value.genre || "").split(",").filter(Boolean);
  const toggleG = g => set("genre", (gl.includes(g) ? gl.filter(x => x !== g) : [...gl, g]).join(","));
  const years = Array.from({ length: 3 }, (_, i) => String(new Date().getFullYear() + i));
  const setPeriod = (mm, yy) => {
    if (!yy) { onChange({ ...value, month: "", year: "", date_from: "", date_to: "" }); return; }
    if (mm) { const last = new Date(+yy, +mm, 0).getDate(); onChange({ ...value, month: `${yy}-${mm}`, year: yy, date_from: `${yy}-${mm}-01`, date_to: `${yy}-${mm}-${String(last).padStart(2, "0")}` }); }
    else onChange({ ...value, month: "", year: yy, date_from: `${yy}-01-01`, date_to: `${yy}-12-31` });
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit?.(); }}
      className="gg-card grid grid-cols-1 md:grid-cols-[1.3fr_1.1fr_1.2fr_auto]" data-testid="search-bar">
      <div className="p-3 md:p-4 border-b md:border-b-0 md:border-r border-foreground">
        <label htmlFor="gg-search-location" className="eyebrow block mb-1.5 md:hidden">{t("location")}</label>
        <div className="flex items-end gap-2">
        <div className="flex-1 min-w-0">
          <label htmlFor="gg-search-location" className="eyebrow mb-1.5 hidden md:block">{t("location")}</label>
          <input id="gg-search-location" data-testid="search-location" className="gg-input !border-0 !p-0 !py-1" placeholder={t("locationPlaceholder")}
            value={value.nearMe ? t("nearMe") : (value.city || "")} onChange={e => onChange({ ...value, city: e.target.value, nearMe: false, lat: undefined, lng: undefined })} />
        </div>
        <button type="button" onClick={onLocate} className={`gg-icon-btn shrink-0 ${value.nearMe ? "!bg-primary !text-primary-foreground" : ""}`} aria-label={t("useMyLocation")} title={t("useMyLocation")} data-testid="search-locate">
          <LocateFixed className={`w-4 h-4 ${locating ? "animate-pulse" : ""}`} />
        </button>
        </div>
      </div>
      <div className="p-3 md:p-4 border-b md:border-b-0 md:border-r border-foreground">
        <label className="eyebrow block mb-1.5">{t("date")}</label>
        <div className="grid grid-cols-[1fr_auto] gap-1">
          <select data-testid="search-month" className="gg-input !border-0 !p-0 !py-1 bg-transparent" value={value.month ? value.month.split("-")[1] : ""} onChange={e => setPeriod(e.target.value, value.year || String(new Date().getFullYear()))}>
            <option value="">{t("anyMonth")}</option>
            {t("monthHeaders").map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
          </select>
          <select data-testid="search-year" className="gg-input !border-0 !p-0 !py-1 bg-transparent" value={value.year || ""} onChange={e => setPeriod(value.month ? value.month.split("-")[1] : "", e.target.value)}>
            <option value="">{t("anyYear")}</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      <div className="p-3 md:p-4 border-b md:border-b-0 md:border-r border-foreground relative">
        <label className="eyebrow block mb-1.5">{t("genre")}</label>
        <button type="button" onClick={() => setGOpen(o => !o)} className="w-full text-left py-1 flex items-center justify-between gap-2 min-h-[28px]" data-testid="search-genre" aria-expanded={gOpen}>
          <span className="flex flex-wrap gap-1 min-w-0">
            {gl.length === 0 ? <span>{t("allGenres")}</span> : gl.map(g => (
              <span key={g} className="inline-flex items-center gap-1 border border-foreground px-1.5 text-[11px] leading-5" data-testid={`genre-tag-${g}`}>{t(`genres.${g}`)}
                <span role="button" aria-label="remove" onClick={e => { e.stopPropagation(); toggleG(g); }} className="hover:text-primary" data-testid={`genre-tag-remove-${g}`}>×</span></span>))}
          </span>
          <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${gOpen ? "rotate-180" : ""}`} />
        </button>
        {gOpen && (
          <div className="absolute left-0 right-0 top-full z-30 bg-background border border-foreground p-1 flex flex-col" data-testid="genre-menu">
            <div className="max-h-64 overflow-y-auto">
            {GENRES.map(g => (
              <label key={g} className="flex items-center gap-2 px-2 min-h-[40px] cursor-pointer hover:bg-muted text-sm">
                <input type="checkbox" className="w-4 h-4" checked={gl.includes(g)} onChange={() => toggleG(g)} data-testid={`genre-opt-${g}`} />{t(`genres.${g}`)}
              </label>
            ))}
            </div>
            <div className="flex justify-between gap-2 p-2 border-t border-foreground">
              <button type="button" onClick={() => set("genre", "")} className="eyebrow underline" data-testid="genre-none">{t("clearAll")}</button>
              <button type="button" onClick={() => setGOpen(false)} className="gg-btn !py-1 !px-3 text-xs" data-testid="genre-done">{t("gotItShort")}</button>
            </div>
          </div>
        )}
      </div>
      <button data-testid="search-submit" type="submit" className="gg-btn-primary !border-0 min-h-[52px] md:min-h-0 md:px-8 uppercase text-base md:text-lg">
        <Search className="w-5 h-5" /><span className="md:hidden">{t("searchEvents")}</span><span className="hidden md:inline">{t("search")}</span>
      </button>
    </form>
  );
}
